import { z } from 'zod';

import { IdSchema, IsoDateSchema } from './common.js';

export const SetTypeSchema = z.enum(['normal', 'warmup', 'failure', 'dropset']);
export type SetType = z.infer<typeof SetTypeSchema>;

export const SetSchema = z.object({
  setNumber: z.number().int().positive(),
  type: SetTypeSchema,
  weight: z.number().min(0).max(10000),
  reps: z.number().int().min(0).max(10000),
  completed: z.boolean(),
  isPersonalRecord: z.boolean().optional(),
});
export type Set = z.infer<typeof SetSchema>;

export const WorkoutExerciseSchema = z.object({
  exerciseId: z.string().min(1).max(128),
  notes: z.string().max(2000).default(''),
  restTimerSeconds: z.number().int().min(0).max(3600).default(90),
  sets: z.array(SetSchema).max(100),
});
export type WorkoutExercise = z.infer<typeof WorkoutExerciseSchema>;

export const WorkoutSchema = z.object({
  id: IdSchema,
  userId: IdSchema,
  title: z.string().min(1).max(200),
  date: IsoDateSchema,
  duration: z.number().int().min(0).max(86400),
  notes: z.string().max(4000).nullable(),
  exercises: z.array(WorkoutExerciseSchema).max(50),
  muscleGroups: z.array(z.string()).default([]),
  totalVolume: z.number().min(0),
  totalSets: z.number().int().min(0),
  // Optional plan linkage. Populated when the workout was started from a
  // training-plan day. planId is a relational FK; planSessionId is a soft
  // FK against Plan.content.sessions[].id. Both must be supplied together
  // (refined at the Create DTO layer); both must be null together on the
  // response shape too.
  planId: IdSchema.nullable().optional(),
  planSessionId: IdSchema.nullable().optional(),
  createdAt: IsoDateSchema,
  updatedAt: IsoDateSchema,
});
export type Workout = z.infer<typeof WorkoutSchema>;

// Both-or-neither plan-linkage refinement. Used by Create and Update DTOs.
function bothOrNeither(
  data: { planId?: string | null; planSessionId?: string | null },
  ctx: z.RefinementCtx,
): void {
  const hasPlanId = data.planId != null;
  const hasSessionId = data.planSessionId != null;
  if (hasPlanId !== hasSessionId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'planId and planSessionId must be provided together',
      path: hasPlanId ? ['planSessionId'] : ['planId'],
    });
  }
}

export const CreateWorkoutSchema = WorkoutSchema.omit({
  id: true,
  userId: true,
  muscleGroups: true,
  totalVolume: true,
  totalSets: true,
  createdAt: true,
  updatedAt: true,
}).superRefine(bothOrNeither);
export type CreateWorkoutInput = z.infer<typeof CreateWorkoutSchema>;

// `.partial()` on the inner object before applying the refine — `superRefine`
// is not preserved across `.partial()` on the wrapped schema, so we partial
// the omitted base then re-attach the refine.
const _UpdateWorkoutBase = WorkoutSchema.omit({
  id: true,
  userId: true,
  muscleGroups: true,
  totalVolume: true,
  totalSets: true,
  createdAt: true,
  updatedAt: true,
}).partial();
export const UpdateWorkoutSchema = _UpdateWorkoutBase.superRefine(bothOrNeither);
export type UpdateWorkoutInput = z.infer<typeof UpdateWorkoutSchema>;

export const ListWorkoutsQuerySchema = z.object({
  from: IsoDateSchema.optional(),
  to: IsoDateSchema.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListWorkoutsQuery = z.infer<typeof ListWorkoutsQuerySchema>;
